import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle2,
  CalendarDays,
  Clock3,
  Receipt,
  LoaderCircle,
  AlertCircle,
} from "lucide-react";
import { useApi } from "../utils/Api";
import studioImage from "../assets/studio-success.jpg";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const formatDate = (value) => {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

const formatTime = (value) => {
  if (!value) return "—";
  const [h, m] = value.split(":");
  const hour = Number(h);
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${suffix}`;
};

export default function StudioRentalPaymentSuccess() {
  const { apiFetch } = useApi();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setError("Missing payment session.");
      setLoading(false);
      return;
    }

    let interval;

    const fetchBooking = async () => {
      try {
        const res = await apiFetch("verify_studio_rental_payment", {
          session_id: sessionId,
        });

        if (!res?.success) {
          throw new Error(res?.message || "Unable to load booking.");
        }

        setBooking(res.data);

        if (res.data.payment_status === "paid" && interval) {
          clearInterval(interval);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
    interval = setInterval(fetchBooking, 3000);

    return () => clearInterval(interval);
  }, [sessionId, apiFetch]);

  const total = useMemo(
    () => money(booking?.rental_plan_price),
    [booking]
  );

  /* ========================= LOADING ========================= */
  if (loading) {
    return (
      <main className="min-h-screen bg-[#1c1c1c] pt-28 px-4 py-10">
        <div className="mx-auto max-w-[520px] rounded-2xl bg-white p-10 text-center">
          <LoaderCircle className="mx-auto h-10 w-10 animate-spin text-black" />
          <p className="mt-4 text-gray-600">Loading booking...</p>
        </div>
      </main>
    );
  }

  /* ========================= ERROR ========================= */
  if (error || !booking) {
    return (
      <main className="min-h-screen bg-[#1c1c1c] pt-28 px-4 py-10">
        <div className="mx-auto max-w-[520px] rounded-2xl bg-white p-10 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-red-500" />

          <h1 className="mt-4 text-xl font-semibold text-black">
            Error
          </h1>

          <p className="mt-2 text-gray-600">{error}</p>

          <Link
            to="/studio-rentals"
            className="mt-6 inline-block rounded-lg bg-pink-500 px-5 py-3 text-white hover:bg-pink-600"
          >
            Back
          </Link>
        </div>
      </main>
    );
  }

  /* ========================= PENDING ========================= */
  if (booking.payment_status !== "paid") {
    return (
      <main className="min-h-screen bg-[#1c1c1c] pt-28 px-4 py-10">
        <div className="mx-auto max-w-[520px] rounded-2xl bg-white p-10 text-center">
          <LoaderCircle className="mx-auto h-10 w-10 animate-spin text-black" />

          <h1 className="mt-6 text-2xl font-semibold text-black">
            Processing your payment...
          </h1>

          <p className="mt-2 text-gray-600">
            Please wait while we confirm your studio rental.
          </p>

          {booking.booking_ref && (
            <p className="mt-4 text-sm text-gray-600">
              Reference:{" "}
              <span className="font-medium text-black">
                {booking.booking_ref}
              </span>
            </p>
          )}
        </div>
      </main>
    );
  }

  /* ========================= SUCCESS ========================= */
  return (
    <main className="min-h-screen bg-[#1c1c1c] pt-28 px-4 py-8">
      <div className="mx-auto max-w-[520px] overflow-hidden rounded-2xl bg-white shadow-sm">
        
        {/* HEADER */}
        <div className="px-6 pb-8 pt-10 text-center">
          <CheckCircle2 size={62} className="mx-auto text-black" />

          <h1 className="mt-5 text-3xl font-semibold text-black">
            Rental Confirmed!
          </h1>

          <p className="mt-2 text-gray-600">
            Your studio rental is confirmed.
          </p>

          {booking.booking_ref && (
            <p className="mt-3 text-sm text-gray-600">
              Reference:{" "}
              <span className="font-medium text-black">
                {booking.booking_ref}
              </span>
            </p>
          )}
        </div>

        {/* IMAGE */}
        <div className="h-[150px] w-full sm:h-[170px]">
          <img
            src={studioImage}
            alt="Studio rental"
            className="h-full w-full object-cover"
          />
        </div>

        {/* DETAILS */}
        <div className="px-6 py-8 text-black">
          <h2 className="text-[21px] font-medium">Rental Details</h2>

          <div className="mt-7 space-y-6">
            {/* PLAN TITLE */}
            <div>
              <p className="text-[17px] font-bold">
                {booking.rental_plan_title || "Studio Rental"}
              </p>
            </div>

            {/* DATE */}
            <div className="flex items-center gap-4">
              <CalendarDays size={24} className="text-black" />
              <p className="text-[17px]">
                {formatDate(booking.booking_date)}
              </p>
            </div>

            {/* TIME */}
            <div className="flex items-center gap-4">
              <Clock3 size={24} className="text-black" />
              <p className="text-[17px]">
                {formatTime(booking.start_time)} -{" "}
                {formatTime(booking.end_time)}
              </p>
            </div>
          </div>

          {/* PAYMENT */}
          <div className="mt-12">
            <h2 className="text-[21px] font-medium">Payment</h2>

            <div className="mt-5 flex items-start gap-4">
              <Receipt size={24} className="mt-1 text-black" />
              <p className="text-[17px]">
                <span className="font-medium">{total}</span> was successfully paid.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-10">
            <Link
              to="/rentals"
              className="inline-flex rounded-xl bg-pink-500 px-5 py-3 text-sm font-semibold text-white hover:bg-pink-600"
            >
              Book another rental
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}